/* eslint-disable */
// BillingContext.js
// Provides plan and feature access info to all components.
// Wrap AppContent in App.js with <BillingProvider>

import { API_URL } from '../../config';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const BillingContext = createContext(null);

// PRICE-005: feature access is no longer derived here. It's read straight
// from billing.lockedFeatures, which the server computes per-plan via
// getPlanFeatures(). The old approach (FEATURE_PLAN + a numeric PLAN_ORDER
// rank) assumed every plan sits on one strict ladder where a higher tier
// always includes everything a lower tier has. Solo breaks that: it
// unlocks reports and AI like Standard does, while capping at 4 units like
// Free — it isn't strictly above or below Standard, it's sideways. Any
// ordinal comparison gets a tier like that wrong no matter where you slot
// it into the order. Trusting the server's locked list avoids the two
// ever drifting out of sync again.

export function BillingProvider({ children }) {
    const { user } = useAuth();
    const [billing, setBilling]   = useState(null);
    const [loading, setLoading]   = useState(true);

    const fetchBilling = useCallback(async () => {
        if (!user) { setLoading(false); return; }
        try {
            const BASE  = API_URL;
            const token = localStorage.getItem('token');
            const res   = await fetch(`${BASE}/api/billing/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Billing fetch failed');
            const data = await res.json();
            setBilling(data);
        } catch {
            // Default to free on error — never block the app. Every field
            // a consumer might read needs a safe default here, not just the
            // ones this file used to touch, since components now read
            // billableUnits/includedUnits/overageUnits directly too.
            setBilling({
                plan: 'free', planName: 'Free', planStatus: 'active',
                propertyCount: 0,
                billableUnits: 0, includedUnits: 4, overageUnits: 0, atUnitLimit: false, overageRate: null,
                lockedFeatures: ['reports','ai','projects','prospects','multi-user','service','contractors'],
                userLimit: 1, userCount: 0, atUserLimit: false,
            });
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { fetchBilling(); }, [fetchBilling]);

    // PRICE-005: reads the server's locked list directly — see note above.
    function canAccess(feature) {
        if (!billing) return false;
        return !(billing.lockedFeatures || []).includes(feature);
    }

    // Renamed from isAtPropertyLimit. Only ever true on hard-capped plans
    // (free/solo) — standard/pro/agency are soft-capped and are billed for
    // overage instead of being blocked, so this is always false for them.
    function isAtUnitLimit() {
        if (!billing) return false;
        return !!billing.atUnitLimit;
    }

    const value = {
        billing, loading, canAccess, isAtUnitLimit,
        refetch: fetchBilling,
        plan:    billing?.plan ?? 'free',
        isPaid:  billing ? billing.plan !== 'free' : false,
    };

    return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
    const ctx = useContext(BillingContext);
    if (!ctx) throw new Error('useBilling must be used inside BillingProvider');
    return ctx;
}
